import { AppInterface } from "types/types"
import AppsListItem from "./AppsListItem"
import Spinner from "./Spinner"
import useT from "utils/useT"
import useGlobalStore from "stores/global"
import AdBanner from "./AdBanner"
import { Virtuoso } from "react-virtuoso"
import { useMemo } from "react"

interface Props {
  title: string
  apps?: AppInterface[]
  isPending?: boolean
  noCard?: boolean
  endReached?: () => void
}

const AppsList = ({ title, apps, isPending, noCard, endReached }: Props) => {
  const t = useT()
  const showAds = useGlobalStore((state) => state.showAds)
  const isDark = useGlobalStore((state) => state.isDark)

  // Pair apps into rows of 2 so Virtuoso virtualizes by row rather than by item.
  // This avoids VirtuosoGrid's per-item height measurement (which causes scroll
  // stutter on variable-height content). Each row renders a div.apps-list-container
  // which the existing CSS grid turns into 2 columns on desktop, 1 on mobile.
  const rows = useMemo(() => {
    if (!apps) return []
    const result: AppInterface[][] = []
    for (let i = 0; i < apps.length; i += 2) {
      result.push(apps.slice(i, i + 2))
    }
    return result
  }, [apps])

  return (
    <div>
      <div className={noCard ? "" : "card card-style"}>
        <div className="content mb-0">
          {title && (
            <h2 dir="auto" className="font-18 font-600 color-highlight pb-3">
              {title}
            </h2>
          )}

          {apps && apps.length > 0 ? (
            // useWindowScroll: virtualizes against the window scroll position
            // rather than an internal scroll container, so the page scrollbar
            // controls the list. overscan=2000px pre-renders rows well before
            // they enter the viewport to prevent blank flashes while scrolling.
            <Virtuoso
              useWindowScroll
              data={rows}
              endReached={endReached}
              overscan={2000}
              itemContent={(rowIndex, row) => (
                <>
                  <div className="apps-list-container">
                    {row.map((app, colIndex) => {
                      const index = rowIndex * 2 + colIndex
                      return (
                        <div key={app.trackId} className="px-2 list-item">
                          <AppsListItem
                            trackId={app.trackId}
                            artworkUrl512={app.artworkUrl512}
                            trackName={app.trackName}
                            genres={app.genres}
                            description={app.description}
                            price={app.price}
                            formattedPrice={app.formattedPrice}
                            submitterPrice={app.submitterPrice}
                            submitterDescription={app.submitterDescription}
                            submitter={app.submitters?.[0] || app.submitter}
                            i={index}
                            appRemoved={app.appRemoved}
                            hasInAppPurchases={app.hasInAppPurchases}
                          />
                        </div>
                      )
                    })}
                  </div>
                  {showAds && rowIndex % 6 === 0 && (
                    <>
                      <div className="divider mb-0"></div>
                      <div style={{ maxHeight: 100 }}>
                        <AdBanner
                          adkey={row[0].trackId + "ad"}
                          data-ad-format="fluid"
                          data-ad-layout-key="-i8+5+s-64+a1"
                          data-ad-client="ca-pub-8210764995783356"
                          data-ad-slot={isDark ? "6859664652" : "5609597164"}
                        />
                      </div>
                    </>
                  )}
                  {rows.length > 1 && rows[rowIndex + 1] !== undefined && (
                    <div className="divider mb-0"></div>
                  )}
                </>
              )}
              components={{
                Footer: () =>
                  isPending ? (
                    <div className="d-flex justify-content-center mb-3">
                      <Spinner size={60} />
                    </div>
                  ) : null,
              }}
            />
          ) : (
            <div className="d-flex justify-content-center p-5">
              {t.emptyList}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AppsList
